import {createSlice, type PayloadAction} from '@reduxjs/toolkit';
import get from 'lodash.get';
import {registrationApi} from '@api/registration/api';
import {userApi} from '@api/user/api';
import {configurationApi} from '@api/configuration/api';
import {getErrorMessage} from '@utils/requests/error';
import {parseJWT} from '@utils/jwtUtils';
import {calculateExpiresAt} from '@utils/tokenManager';
import {
    fetchSessionInfo,
    fetchUserAttributes,
    setAccessToken,
    setTokenExpiresAt
} from '@components/Login/userSlice';
import {fetchTransactionsSummary} from '@components/Transactions/transactionsSlice';
import {loadTranslations, setLocale, setTheme} from '@components/appSlice';
import {getUiTheme} from '@constants/themeMapping';
import {SMS_RESEND_COOLDOWN_SEC} from '@constants/registration';
import type {
    StoreRegistrationRequest,
    RegistrationFormValues,
    FieldValidationError,
    ServerValidationErrors,
    RegistrationStatusResource
} from '@api/registration/api.d';
import type {SecuritySettingsResource} from '@api/configuration/api.d';

interface AccountRegistrationState {
    registrationId: string | null;
    registrationStatus: RegistrationStatusResource | null;
    formValues: RegistrationFormValues | null;
    cooldownRemainingSeconds: number;
    securityPolicy: Partial<SecuritySettingsResource> | null;
    isSubmitting: boolean;
    isConfirmingEmail: boolean;
    isVerifyingSms: boolean;
    isResendingSms: boolean;
    isCompletingRegistration: boolean;
    isSettingPassword: boolean;
    isFetchingSecurityPolicy: boolean;
    isFetchingRegistrationStatus: boolean;
    fetchRegistrationStatusError: FieldValidationError | null;
    isEmailValidating: boolean;
    isEmailValid: boolean;
    emailValidationError: string | null;
    isPhoneValidating: boolean;
    isPhoneValid: boolean;
    phoneValidationError: string | null;
    submitError: FieldValidationError | null;
    serverValidationErrors: ServerValidationErrors | null;
    confirmEmailError: FieldValidationError | null;
    verifySmsError: FieldValidationError | null;
    resendSmsError: FieldValidationError | null;
    completeError: FieldValidationError | null;
    setPasswordError: FieldValidationError | null;
    fetchSecurityPolicyError: FieldValidationError | null;
}

const initialState: AccountRegistrationState = {
    registrationId: null,
    registrationStatus: null,
    formValues: null,
    cooldownRemainingSeconds: 0,
    securityPolicy: null,
    isSubmitting: false,
    isConfirmingEmail: false,
    isVerifyingSms: false,
    isResendingSms: false,
    isCompletingRegistration: false,
    isSettingPassword: false,
    isFetchingSecurityPolicy: false,
    isFetchingRegistrationStatus: false,
    fetchRegistrationStatusError: null,
    isEmailValidating: false,
    isEmailValid: false,
    emailValidationError: null,
    isPhoneValidating: false,
    isPhoneValid: false,
    phoneValidationError: null,
    submitError: null,
    serverValidationErrors: null,
    confirmEmailError: null,
    verifySmsError: null,
    resendSmsError: null,
    completeError: null,
    setPasswordError: null,
    fetchSecurityPolicyError: null
};

const accountRegistrationSlice = createSlice({
    name: 'accountRegistration',
    initialState,
    reducers: {
        setFormValues: (state, action: PayloadAction<RegistrationFormValues | null>) => {
            state.formValues = action.payload;
        },
        setRegistrationId: (state, action: PayloadAction<string | null>) => {
            state.registrationId = action.payload;
        },
        setCooldownRemainingSeconds: (state, action: PayloadAction<number>) => {
            state.cooldownRemainingSeconds = action.payload;
        },
        setSecurityPolicy: (state, action: PayloadAction<Partial<SecuritySettingsResource> | null>) => {
            state.securityPolicy = action.payload;
        },
        setIsSubmitting: (state, action: PayloadAction<boolean>) => {
            state.isSubmitting = action.payload;
        },
        setIsConfirmingEmail: (state, action: PayloadAction<boolean>) => {
            state.isConfirmingEmail = action.payload;
        },
        setIsVerifyingSms: (state, action: PayloadAction<boolean>) => {
            state.isVerifyingSms = action.payload;
        },
        setIsResendingSms: (state, action: PayloadAction<boolean>) => {
            state.isResendingSms = action.payload;
        },
        setIsCompletingRegistration: (state, action: PayloadAction<boolean>) => {
            state.isCompletingRegistration = action.payload;
        },
        setIsSettingPassword: (state, action: PayloadAction<boolean>) => {
            state.isSettingPassword = action.payload;
        },
        setIsFetchingSecurityPolicy: (state, action: PayloadAction<boolean>) => {
            state.isFetchingSecurityPolicy = action.payload;
        },
        setIsFetchingRegistrationStatus: (state, action: PayloadAction<boolean>) => {
            state.isFetchingRegistrationStatus = action.payload;
        },
        setRegistrationStatus: (state, action: PayloadAction<RegistrationStatusResource | null>) => {
            state.registrationStatus = action.payload;
        },
        setFetchRegistrationStatusError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.fetchRegistrationStatusError = action.payload;
        },
        setIsEmailValidating: (state, action: PayloadAction<boolean>) => {
            state.isEmailValidating = action.payload;
        },
        setIsEmailValid: (state, action: PayloadAction<boolean>) => {
            state.isEmailValid = action.payload;
        },
        setEmailValidationError: (state, action: PayloadAction<string | null>) => {
            state.emailValidationError = action.payload;
        },
        setIsPhoneValidating: (state, action: PayloadAction<boolean>) => {
            state.isPhoneValidating = action.payload;
        },
        setIsPhoneValid: (state, action: PayloadAction<boolean>) => {
            state.isPhoneValid = action.payload;
        },
        setPhoneValidationError: (state, action: PayloadAction<string | null>) => {
            state.phoneValidationError = action.payload;
        },
        setSubmitError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.submitError = action.payload;
        },
        setServerValidationErrors: (state, action: PayloadAction<ServerValidationErrors | null>) => {
            state.serverValidationErrors = action.payload;
        },
        setConfirmEmailError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.confirmEmailError = action.payload;
        },
        setVerifySmsError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.verifySmsError = action.payload;
        },
        setResendSmsError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.resendSmsError = action.payload;
        },
        setCompleteError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.completeError = action.payload;
        },
        setSetPasswordError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.setPasswordError = action.payload;
        },
        setFetchSecurityPolicyError: (state, action: PayloadAction<FieldValidationError | null>) => {
            state.fetchSecurityPolicyError = action.payload;
        },
        resetAccountRegistrationState: () => initialState,
        clearErrors: (state) => {
            state.submitError = null;
            state.serverValidationErrors = null;
            state.confirmEmailError = null;
            state.verifySmsError = null;
            state.resendSmsError = null;
            state.completeError = null;
            state.setPasswordError = null;
            state.fetchSecurityPolicyError = null;
            state.fetchRegistrationStatusError = null;
            state.emailValidationError = null;
            state.phoneValidationError = null;
            state.isEmailValid = false;
            state.isPhoneValid = false;
        }
    }
});

export const {
    setFormValues,
    setRegistrationId,
    setRegistrationStatus,
    setCooldownRemainingSeconds,
    setSecurityPolicy,
    setIsSubmitting,
    setIsConfirmingEmail,
    setIsVerifyingSms,
    setIsResendingSms,
    setIsCompletingRegistration,
    setIsSettingPassword,
    setIsFetchingSecurityPolicy,
    setIsFetchingRegistrationStatus,
    setFetchRegistrationStatusError,
    setIsEmailValidating,
    setIsEmailValid,
    setEmailValidationError,
    setIsPhoneValidating,
    setIsPhoneValid,
    setPhoneValidationError,
    setSubmitError,
    setServerValidationErrors,
    setConfirmEmailError,
    setVerifySmsError,
    setResendSmsError,
    setCompleteError,
    setSetPasswordError,
    setFetchSecurityPolicyError,
    resetAccountRegistrationState,
    clearErrors
} = accountRegistrationSlice.actions;

export const validateEmailField = (email: string) => async (dispatch: any) => {
    dispatch(setIsEmailValidating(true));
    dispatch(setEmailValidationError(null));

    try {
        await registrationApi.validateEmail({data: {email}});
        dispatch(setIsEmailValid(true));
        dispatch(setIsEmailValidating(false));
        return true;
    } catch (err: any) {
        const message = err?.response?.data?.message || err?.data?.message || 'Email validation failed';
        dispatch(setEmailValidationError(message));
        dispatch(setIsEmailValid(false));
        dispatch(setIsEmailValidating(false));
        return false;
    }
};

export const validatePhoneField = (phone: string) => async (dispatch: any) => {
    dispatch(setIsPhoneValidating(true));
    dispatch(setPhoneValidationError(null));

    try {
        await registrationApi.validatePhone({data: {phone}});
        dispatch(setIsPhoneValid(true));
        dispatch(setIsPhoneValidating(false));
        return true;
    } catch (err: any) {
        const message = err?.response?.data?.message || err?.data?.message || 'Phone validation failed';
        dispatch(setPhoneValidationError(message));
        dispatch(setIsPhoneValid(false));
        dispatch(setIsPhoneValidating(false));
        return false;
    }
};

export const fetchRegistrationStatus = (registrationId: string) => async (dispatch: any) => {
    dispatch(setIsFetchingRegistrationStatus(true));
    dispatch(setFetchRegistrationStatusError(null));

    try {
        const response = await registrationApi.getRegistrationStatus({registrationId});
        const status = (response as unknown as {data: RegistrationStatusResource}).data;

        dispatch(setRegistrationStatus(status));
        dispatch(setRegistrationId(registrationId));
        dispatch(setIsFetchingRegistrationStatus(false));

        return status;
    } catch (err) {
        dispatch(setIsFetchingRegistrationStatus(false));
        dispatch(setFetchRegistrationStatusError(getErrorMessage(err)));
        return null;
    }
};

export const submitRegistration = (
    formData: RegistrationFormValues,
    captchaToken: string
) => async (dispatch: any) => {
    dispatch(setIsSubmitting(true));
    dispatch(setSubmitError(null));
    dispatch(setServerValidationErrors(null));

    try {
        const requestData: StoreRegistrationRequest = {
            firstname: formData.firstName,
            lastname: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            title: formData.title,
            industry: formData.industry || '',
            language: formData.language,
            country: formData.country,
            captcha_token: captchaToken
        };

        const registrationData = await registrationApi.createRegistration({data: requestData}) as unknown as {registration_id: string};

        dispatch(setRegistrationId(registrationData.registration_id));
        dispatch(setFormValues(formData));
        dispatch(setIsSubmitting(false));

        return registrationData;
    } catch (err: any) {
        dispatch(setIsSubmitting(false));

        const responseData = err?.response?.data || err?.data || {};
        const serverErrors = responseData.errors as ServerValidationErrors | undefined;

        if (serverErrors && Object.keys(serverErrors).length > 0) {
            dispatch(setServerValidationErrors(serverErrors));
        }

        dispatch(setSubmitError(getErrorMessage(err)));
        throw err;
    }
};

export const confirmEmailAndSendSms = (
    registrationId: string,
    token: string
) => async (dispatch: any) => {
    dispatch(setIsConfirmingEmail(true));
    dispatch(setConfirmEmailError(null));

    try {
        await registrationApi.confirmEmail({
            registrationId,
            data: {token}
        });

        dispatch(setCooldownRemainingSeconds(SMS_RESEND_COOLDOWN_SEC));
        dispatch(setRegistrationId(registrationId));
        dispatch(setIsConfirmingEmail(false));

        return true;
    } catch (err) {
        dispatch(setIsConfirmingEmail(false));
        dispatch(setConfirmEmailError(getErrorMessage(err)));
        return false;
    }
};

export const verifySmsCode = (
    registrationId: string,
    secret: string
) => async (dispatch: any) => {
    dispatch(setIsVerifyingSms(true));
    dispatch(setVerifySmsError(null));

    try {
        await registrationApi.verifySms({
            registrationId,
            data: {secret: parseInt(secret, 10)}
        });

        dispatch(setIsVerifyingSms(false));
        return true;
    } catch (err) {
        dispatch(setIsVerifyingSms(false));
        dispatch(setVerifySmsError(getErrorMessage(err)));
        return false;
    }
};

export const resendSmsCode = (registrationId: string) => async (dispatch: any) => {
    dispatch(setIsResendingSms(true));
    dispatch(setResendSmsError(null));

    try {
        await registrationApi.resendSms({registrationId});

        dispatch(setCooldownRemainingSeconds(SMS_RESEND_COOLDOWN_SEC));
        dispatch(setIsResendingSms(false));
        return true;
    } catch (err) {
        dispatch(setIsResendingSms(false));
        dispatch(setResendSmsError(getErrorMessage(err)));
        return false;
    }
};

export const completeRegistration = (
    registrationId: string,
    password: string
) => async (dispatch: any) => {
    dispatch(setIsCompletingRegistration(true));
    dispatch(setCompleteError(null));

    try {
        const response = await registrationApi.completeRegistration({
            registrationId,
            data: {
                password,
                password_confirmation: password
            }
        });

        const tokenData = response.data;
        const expiresAt = calculateExpiresAt(tokenData.expires_in);

        dispatch(setAccessToken(tokenData.access_token));
        dispatch(setTokenExpiresAt(expiresAt));

        const session = await dispatch(fetchSessionInfo());
        const attributes = await dispatch(fetchUserAttributes());

        if (session) {
            const locale = get(session, 'user.language');
            const theme = getUiTheme(attributes?.default_theme);

            await dispatch(fetchTransactionsSummary());

            dispatch(setLocale({locale, shouldSetCookie: true}));
            dispatch(setTheme({theme, shouldSetCookie: true}));
            dispatch(loadTranslations());
        }

        dispatch(setIsCompletingRegistration(false));

        return true;
    } catch (err) {
        dispatch(setIsCompletingRegistration(false));
        dispatch(setCompleteError(getErrorMessage(err)));
        return false;
    }
};

export const fetchSecurityPolicyForRegistration = (userId: string) => async (dispatch: any) => {
    dispatch(setIsFetchingSecurityPolicy(true));
    dispatch(setFetchSecurityPolicyError(null));

    try {
        const response = await configurationApi.getUserSecurityPolicy(userId);
        const securityPolicy = ((response as any)?.data ?? response) as Partial<SecuritySettingsResource>;

        dispatch(setSecurityPolicy(securityPolicy));
        dispatch(setIsFetchingSecurityPolicy(false));

        return securityPolicy;
    } catch (err) {
        dispatch(setIsFetchingSecurityPolicy(false));
        dispatch(setFetchSecurityPolicyError(getErrorMessage(err)));
        return false;
    }
};

export const setPasswordAndEstablishSession = (
    userId: string,
    password: string
) => async (dispatch: any) => {
    dispatch(setIsSettingPassword(true));
    dispatch(setSetPasswordError(null));

    try {
        await userApi.resetPassword({
            userId,
            data: {
                new_password: password,
                new_password_confirmation: password
            }
        });

        await dispatch(fetchSessionInfo());
        dispatch(setIsSettingPassword(false));

        return true;
    } catch (err) {
        dispatch(setIsSettingPassword(false));
        dispatch(setSetPasswordError(getErrorMessage(err)));
        return false;
    }
};

export const getUserIdFromToken = () => (_dispatch: any, getState: any) => {
    const {accessToken} = getState().user;
    if (!accessToken) {
        return null;
    }

    const payload = parseJWT(accessToken);
    return payload?.sub || payload?.user_id || null;
};

export const selectRegistrationId = (state: any) => state.accountRegistration.registrationId;
export const selectFormValues = (state: any) => state.accountRegistration.formValues;
export const selectCooldownRemainingSeconds = (state: any) => state.accountRegistration.cooldownRemainingSeconds;
export const selectSecurityPolicy = (state: any) => state.accountRegistration.securityPolicy;
export const selectIsSubmitting = (state: any) => state.accountRegistration.isSubmitting;
export const selectIsConfirmingEmail = (state: any) => state.accountRegistration.isConfirmingEmail;
export const selectIsVerifyingSms = (state: any) => state.accountRegistration.isVerifyingSms;
export const selectIsResendingSms = (state: any) => state.accountRegistration.isResendingSms;
export const selectIsCompletingRegistration = (state: any) => state.accountRegistration.isCompletingRegistration;
export const selectIsSettingPassword = (state: any) => state.accountRegistration.isSettingPassword;
export const selectIsFetchingSecurityPolicy = (state: any) => state.accountRegistration.isFetchingSecurityPolicy;
export const selectIsFetchingRegistrationStatus = (state: any) => state.accountRegistration.isFetchingRegistrationStatus;
export const selectRegistrationStatus = (state: any) => state.accountRegistration.registrationStatus;
export const selectFetchRegistrationStatusError = (state: any) => state.accountRegistration.fetchRegistrationStatusError;
export const selectIsEmailValidating = (state: any) => state.accountRegistration.isEmailValidating;
export const selectIsEmailValid = (state: any) => state.accountRegistration.isEmailValid;
export const selectEmailValidationError = (state: any) => state.accountRegistration.emailValidationError;
export const selectIsPhoneValidating = (state: any) => state.accountRegistration.isPhoneValidating;
export const selectIsPhoneValid = (state: any) => state.accountRegistration.isPhoneValid;
export const selectPhoneValidationError = (state: any) => state.accountRegistration.phoneValidationError;
export const selectSubmitError = (state: any) => state.accountRegistration.submitError;
export const selectServerValidationErrors = (state: any) => state.accountRegistration.serverValidationErrors;
export const selectConfirmEmailError = (state: any) => state.accountRegistration.confirmEmailError;
export const selectVerifySmsError = (state: any) => state.accountRegistration.verifySmsError;
export const selectResendSmsError = (state: any) => state.accountRegistration.resendSmsError;
export const selectCompleteError = (state: any) => state.accountRegistration.completeError;
export const selectSetPasswordError = (state: any) => state.accountRegistration.setPasswordError;
export const selectFetchSecurityPolicyError = (state: any) => state.accountRegistration.fetchSecurityPolicyError;

export const selectIsRegistrationInProgress = (state: any) => {
    return state.accountRegistration.registrationId !== null;
};

export default accountRegistrationSlice.reducer;
